import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, RefreshCw, Plus, Trash2, Save, Users, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export function EmotionalMapView({ scene, onUpdate, onNavigate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [rows, setRows] = useState(scene.emotional_map || []);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!scene.mirroring) {
    return (
      <div>
        <div className="paper p-10 text-center">
          <Users className="mx-auto text-amber-500"/>
          <div className="serif text-2xl text-stone-100 mt-3">{t("map.need_mirror")}</div>
        </div>
        {onNavigate && (
          <div className="mt-8 pt-6 border-t border-stone-800/60 flex justify-end">
            <button data-testid="nav-to-zapalnik-btn" onClick={() => onNavigate("zapalnik")} className="pill-btn">
              {t("nav.to_zapalnik")} <ArrowRight size={14}/>
            </button>
          </div>
        )}
      </div>
    );
  }

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/emotional-map`);
      setRows(r.data.mapa);
      onUpdate?.({ emotional_map: r.data.mapa });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("an.fail"));
    } finally { setBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/scenes/${scene.scene_id}/emotional-map`, { mapa: rows });
      onUpdate?.({ emotional_map: rows });
      toast.success(t("map.saved"));
    } catch {
      toast.error(t("mir.fail"));
    } finally { setSaving(false); }
  };

  const update = (i, key, val) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const remove = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const add = () => setRows(rs => [...rs, { w_scenie: "", w_twoim_zyciu: "" }]);

  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap">{t("map.eyebrow")}</div>
          <h2 className="serif text-3xl text-stone-100 mt-1">{t("map.title")}</h2>
          <p className="text-stone-400 text-sm mt-2 max-w-2xl">{t("map.subtitle")}</p>
        </div>
        <button data-testid="map-generate-btn" onClick={generate} disabled={busy} className="pill-btn">
          {busy ? <><RefreshCw size={14} className="animate-spin"/> {t("map.generating")}</> : (rows.length ? <><RefreshCw size={14}/> {t("map.regenerate")}</> : <><Sparkles size={14}/> {t("map.generate")}</>)}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="paper p-10 text-center mt-8">
          <Sparkles className="mx-auto text-amber-500"/>
          <div className="text-stone-400 text-sm mt-3">{t("map.empty")}</div>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {rows.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * i }}
              data-testid={`map-row-${i}`}
              className="paper p-4 grid md:grid-cols-2 gap-3 relative"
            >
              <div>
                <div className="label-cap text-amber-500">{t("map.in_scene")}</div>
                <textarea
                  data-testid={`map-in-scene-${i}`}
                  value={r.w_scenie}
                  onChange={(e)=>update(i, "w_scenie", e.target.value)}
                  rows={3}
                  className="script-input mono text-sm mt-1"
                />
              </div>
              <div>
                <div className="label-cap">{t("map.in_your_life")}</div>
                <textarea
                  data-testid={`map-in-life-${i}`}
                  value={r.w_twoim_zyciu}
                  onChange={(e)=>update(i, "w_twoim_zyciu", e.target.value)}
                  rows={3}
                  className="script-input mono text-sm mt-1"
                />
              </div>
              <button onClick={()=>remove(i)} className="absolute top-2 right-2 text-stone-500 hover:text-rose-400 p-1">
                <Trash2 size={14}/>
              </button>
            </motion.div>
          ))}
          <div className="flex gap-2 flex-wrap">
            <button data-testid="map-add-btn" onClick={add} className="ghost-btn"><Plus size={14}/> {t("map.add_row")}</button>
            <button data-testid="map-save-btn" onClick={save} disabled={saving} className="pill-btn"><Save size={14}/> {saving ? t("new.saving") : t("map.save")}</button>
          </div>
        </div>
      )}

      {onNavigate && (
        <div className="mt-8 pt-6 border-t border-stone-800/60 flex justify-end">
          <button data-testid="nav-to-zapalnik-btn" onClick={() => onNavigate("zapalnik")} className="pill-btn">
            {t("nav.to_zapalnik")} <ArrowRight size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}
